import React, { useContext, useEffect, useState } from 'react';
import { NavMenu } from './NavMenu';
import { Divider, Card, CardContent, Container, Typography, Button, List, ListItem, ListItemText, ListItemAvatar, Avatar, CardHeader, MenuItem } from '@mui/material';
import StopsContext from '../contexts/StopsContext';
import FieldActivityContext from '../contexts/FieldActivityContext';
import { useParams, Link } from 'react-router-dom';
import { ConnectionAwareBackButton } from './ConnectionStatusBlip';
import Moment from 'moment';
import PWDService from '../utils/apiClient';
import FieldActivityFinder from '../utils/updateHelper';
import SubjectOutlinedIcon from '@mui/icons-material/SubjectOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import { ContainerIcon, RouteButtonIcon, CrmSrIcon } from './SvgAppIcons';
import { useSnackbar } from 'notistack';
import ScrollToTop from '../utils/scrollToTopOnMount';
import FiledActivityMenuButton from "./FiledActivityMenuButton";
import { BottomNav } from './BottomNav';
import { useIsMountedRef } from '../utils/customHooks';

//For debug
// eslint-disable-next-line
import FieldActivityDetailsTestData from '../test_data/faDetailsTestData';

const GOOGLE_MAPS_API_BASE_URL = 'https://www.google.com/maps/dir/?api=1&destination=';


const FieldActivityDetailScreenHeader = (props) => {

    //code to prevent user from submitting a Foreman Investigate again
    let disableBool = false;
    if (props.fa.Status === 'PENDING' && props.fa.ForemanInvestigateFlag) {
        disableBool = true;
    }
    if (props.fa.Status === 'COMPLETED') {
        disableBool = true;
    }

    return (
        <NavMenu>
            <ConnectionAwareBackButton />
            <Typography sx={{ flexGrow: 1, paddingLeft: "15px", textAlign: 'left' }}>
                {props.title}
            </Typography>
            <div>
            {props.loaded &&
                <FiledActivityMenuButton>
                    <MenuItem sx={{ borderBottom: "1px solid grey" }} disabled={props.fa.Status === 'COMPLETED' ? true: false} component={Link} to="/confirm-choice/container-not-out">
                        Container Not Out
                    </MenuItem>
                    <MenuItem disabled={disableBool ? true: false} component={Link} to="/confirm-choice/foreman-investigate">
                        Foreman Investigate
                    </MenuItem>
                </FiledActivityMenuButton>
            }
            </div>
        </NavMenu>
    );
};

const BaseListItem = (props) => {
    return (
        <React.Fragment>
            <ListItem
                style={{paddingTop: 0}}
                alignItems="flex-start"
            >
                <ListItemAvatar>
                    <Avatar>
                        {props.avatarIcon}
                    </Avatar>
                </ListItemAvatar>
                <ListItemText
                    primary={
                        <React.Fragment>
                            <Typography
                                component="span"
                                variant="body2"
                                color="textSecondary"
                                style={{fontSize: '1rem'}}
                            >
                                {props.primaryText}
                            </Typography>
                        </React.Fragment>
                    }
                    secondary={
                        <React.Fragment>
                            <Typography
                                component="span"
                                variant="body1"
                                color="textPrimary"
                                style={{fontSize: '1.25rem'}}
                            >
                                {props.secondaryText}
                            </Typography>
                        </React.Fragment>
                    }
                >
                </ListItemText>
            </ListItem>
            <Divider variant="inset" />
        </React.Fragment>
    );
};

const StopDetailsList = (props) => {
    let faObj = props.fa;

    return (
        <List>
            <BaseListItem avatarIcon={<ContainerIcon />} primaryText="Container Action / Count / Size / Type" secondaryText={`${faObj.ContainerActionDisplay} / ${faObj.ContainerCount} / ${faObj.ContainerSize} / ${faObj.ContainerType}`} />
            <BaseListItem avatarIcon={<SubjectOutlinedIcon />} primaryText="Instructions" secondaryText={faObj.Instructions} />
            <BaseListItem avatarIcon={<TodayOutlinedIcon />} primaryText="Date Scheduled" secondaryText={Moment(faObj.DateScheduled).format('L')} />
            {faObj.PartToRepair &&
                <BaseListItem avatarIcon={<BuildOutlinedIcon />} primaryText="Part(s) to Repair" secondaryText={faObj.PartToRepair} />
            }
            <BaseListItem avatarIcon={"CCB"} avatarIconFontSize='1.1rem' primaryText="Field Activity" secondaryText={faObj.FieldActivityId} />
            {faObj.ServiceRequestNumber &&
                <BaseListItem avatarIcon={<CrmSrIcon />} primaryText="Service Request" secondaryText={faObj.ServiceRequestNumber} />
            }
            {faObj.CcbLogMessages.length > 0 &&
                <BaseListItem avatarIcon={<FormatListBulletedRoundedIcon />} primaryText="CCB Logs" secondaryText={faObj.CcbLogMessages.map(function(log, idx)
                    {
                            return (<span style={{display: 'block'}} key={idx}>-{log.message}</span>);
                    })} />
            }
        </List>
    );
};

const FieldActivityCard = (props) => {

    let faObj = props.fa;
    var encodedAddress = encodeURI(faObj.Address);

    const openGoogleMaps = (url) => {
        let newWindow = window.open(url, '_blank');
        if (newWindow) newWindow.opener = null;
    };

    return (
        <Card elevation={0}>
            <CardHeader
                sx={{ flexGrow: 1, paddingLeft: "15px", textAlign: 'left' }}
                avatar={
                    <div onClick={() => openGoogleMaps(`${GOOGLE_MAPS_API_BASE_URL}${encodedAddress}`)}>
                        <Avatar>
                            <RouteButtonIcon />
                        </Avatar>
                    </div>
                }
                title={faObj.Address}
                subheader={`${faObj.Latitude}, ${faObj.Longitude}`}
                titleTypographyProps={{variant: 'h4' }}
            />
            <CardContent sx={{ flex: '1 0 auto', paddingTop: "0px", paddingRight: "10px", paddingLeft: "10px" }}>
                <StopDetailsList fa={faObj} />
            </CardContent>
        </Card>
    );
};


const ResolutionButtonsCard = (props) => {
    let successBtnText = '';
    switch(props.cat) {
        case 'PWREPAIR':
            successBtnText = 'REPAIRED';
            break;
        case 'PWREPLACE':
            successBtnText = 'REPLACED';
            break;
        case 'PWREMOVE':
            successBtnText = 'REMOVED';
            break;
        case 'PWDELIVER':
            successBtnText = 'DELIVERED';
            break;
        default:
            successBtnText = props.cat;
    }

    return (
        <BottomNav>
            <Button
                disabled={props.faStatus === "COMPLETED" || props.faStatus === "CANCELLED" ? true : false}
                variant="contained"
                component={Link}
                to="/confirm-choice/work-complete"
            >
                {successBtnText}
            </Button>
        </BottomNav>
    );
};


const FieldActivityDetailScreen = (props) => {
    const { stops, setStops } = useContext(StopsContext);
    const { fieldActivityId } = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const { setFieldActivityId } = useContext(FieldActivityContext);
    const [loaded, setLoaded] = useState(true);
    const isMountedRef = useIsMountedRef();

    const fa = stops.find(({FieldActivityId}) => FieldActivityId === fieldActivityId);

    var bottomButtonText = '';

    useEffect(() => {
        setFieldActivityId(fieldActivityId);
    },[fieldActivityId, setFieldActivityId]);

    useEffect(() => {
        //setLoading(true);
        let now = Moment().format();
        setLoaded(false);
        //Changing this because I believe it is necessary to always check the latest from CCB for log seq num and other FA props
        //Synced prop prevents unnecessary api calls
        //if (fa.Synced === false) {
        if (true) {

            const fetchFaData = PWDService.getFieldActivityDetails(fa.FieldActivityId);
            const fetchFaLog = PWDService.getFieldActivityLog(fa.FieldActivityId);

            try {
                Promise.all([fetchFaData, fetchFaLog]).then(responseArray => {
                    if(isMountedRef.current){

                        const newStops = [...stops];
                        var faForUpdate = FieldActivityFinder(newStops, fieldActivityId);
                        const faDetailsResp = responseArray[0];
                        const faLogResp = responseArray[1];

                        if (faDetailsResp.status === 200) {
                            faForUpdate.update.Synced = true;
                            faForUpdate.update.Status = faDetailsResp.data.fieldActivityStatus.toUpperCase();
                            faForUpdate.update.MainPersonId = faDetailsResp.data.mainPersonId;
                            faForUpdate.update.ModifiedDateTime = now;
                        } else if (faDetailsResp.status === 404) {
                            //FA NOT FOUND
                            faForUpdate.update.Status = 'FA NOT FOUND';
                            let message = `Field Activity ${fieldActivityId} not found`;
                            enqueueSnackbar(message, {
                                variant: 'error',
                            });
                        } else if (faDetailsResp.status === 500) {
                            //there's a problem with CC&B...
                            let message = `Error - CC&B services not available.`;
                            enqueueSnackbar(message, {
                                variant: 'error',
                            });
                        }

                        if (faLogResp.status === 200) {
                            faForUpdate.update.FieldActivityRemarkValue = faLogResp.data.lastFaRemark;
                            faForUpdate.update.LastLogSequenceNumber = parseInt(faLogResp.data.lastSequence);
                            faForUpdate.update.CcbLogMessages = faLogResp.data.logs;

                            switch (faLogResp.data.lastFaRemark) {
                                case "PWREMC":
                                    faForUpdate.update.FieldActivityRemark = "REMOVED";
                                    break;
                                case "PWDELCON":
                                    faForUpdate.update.FieldActivityRemark = "DELIVERED";
                                    break;
                                case "PWREPA":
                                    faForUpdate.update.FieldActivityRemark = "REPAIRED";
                                    break;
                                case "PWREPL":
                                    faForUpdate.update.FieldActivityRemark = "REPLACED";
                                    break;
                                case "PWCNOUT":
                                    faForUpdate.update.FieldActivityRemark = "CONTAINER NOT OUT";
                                    break;
                                case "PWADONE":
                                    faForUpdate.update.FieldActivityRemark = "DONE";
                                    break;
                                default:
                                    faForUpdate.update.FieldActivityRemark = "";
                            }
                        }
                        //update fa in app
                        newStops[faForUpdate.index] = faForUpdate.update;
                        setStops(newStops);
                        setLoaded(true);
                    }
                }).catch(error => {
                    console.error("Error in FA details promise: ", error.message);
                    setLoaded(true);
                });
            }
            catch (error) {
                console.error("caught error when attempting Promise.All: ", error);
                setLoaded(true);
            }
        }
    },[isMountedRef]);

    if (fa.Status === 'COMPLETED') {
        bottomButtonText = fa.FieldActivityRemark;
    } else {
        bottomButtonText = fa.ContainerActionType;
    }

    const faDetailsScreenTitle = `Service Location Details`;
    return (
        <div id="FieldActivityDetailScreen">
            <ScrollToTop />
            <FieldActivityDetailScreenHeader fa={fa} title={faDetailsScreenTitle} actionType={fa.ContainerActionDisplay} loaded={loaded}/>
            <Container maxWidth={false} style={{marginTop: 74, paddingLeft: 2, paddingRight: 2 }}>
                <FieldActivityCard fa={fa}/>
                {loaded &&
                    <ResolutionButtonsCard cat={bottomButtonText} faStatus={fa.Status}/>
                }
            </Container>
        </div>
    );
};

export default FieldActivityDetailScreen;